/**
 * Simulation Progress Tracking
 *
 * Parses EnergyPlus console output to extract progress information
 * and estimate remaining time.
 */

/**
 * Simulation phases
 */
export type SimulationPhase =
  | 'initializing'
  | 'warmup'
  | 'sizing'
  | 'simulation'
  | 'postprocess'
  | 'complete'
  | 'error';

/**
 * Progress update information
 */
export interface ProgressUpdate {
  /** Current simulation phase */
  phase: SimulationPhase;
  /** Progress percentage (0-100) */
  progress: number;
  /** Current status message */
  message: string;
  /** Time when this update occurred */
  timestamp: number;
}

/**
 * Time estimation data
 */
export interface TimeEstimate {
  /** Elapsed time in milliseconds */
  elapsed: number;
  /** Estimated remaining time in milliseconds (null if unknown) */
  remaining: number | null;
  /** Estimated total time in milliseconds (null if unknown) */
  total: number | null;
}

/**
 * Console line with severity information
 */
export interface ConsoleLine {
  /** The line text */
  text: string;
  /** Timestamp when received */
  timestamp: number;
  /** Line type/severity */
  type: 'info' | 'warning' | 'severe' | 'fatal' | 'success';
}

/**
 * Progress pattern definitions
 */
interface ProgressPattern {
  pattern: RegExp;
  phase: SimulationPhase;
  getProgress?: (match: RegExpMatchArray) => number;
  getMessage?: (match: RegExpMatchArray) => string;
}

/**
 * Patterns for parsing EnergyPlus output
 */
const PROGRESS_PATTERNS: ProgressPattern[] = [
  // Initialization
  {
    pattern: /^Initializing/i,
    phase: 'initializing',
    getProgress: () => 0,
  },
  {
    pattern: /^Processing (Input|Weather|Schedule)/i,
    phase: 'initializing',
    getProgress: () => 5,
    getMessage: (m) => `Processing ${m[1] ?? ''}...`,
  },

  // Warmup
  {
    pattern: /^Warming up \{(\d+)\}/i,
    phase: 'warmup',
    getProgress: (m) => Math.min(10 + parseInt(m[1] ?? '0', 10) * 2, 20),
    getMessage: (m) => `Warmup period ${m[1] ?? ''}`,
  },

  // Sizing
  {
    pattern: /^Performing Zone Sizing/i,
    phase: 'sizing',
    getProgress: () => 20,
  },
  {
    pattern: /^Performing System Sizing/i,
    phase: 'sizing',
    getProgress: () => 25,
  },
  {
    pattern: /^Performing Plant Sizing/i,
    phase: 'sizing',
    getProgress: () => 30,
  },

  // Simulation
  {
    pattern: /^Starting Simulation at (.+)/i,
    phase: 'simulation',
    getProgress: () => 35,
    getMessage: (m) => `Starting simulation: ${m[1] ?? ''}`,
  },
  {
    pattern: /^Updating Coverage of Annual Simulation at (\d+\.?\d*)%/i,
    phase: 'simulation',
    getProgress: (m) => 35 + (parseFloat(m[1] ?? '0') * 0.55),
    getMessage: (m) => `Simulation progress: ${parseFloat(m[1] ?? '0').toFixed(1)}%`,
  },
  {
    pattern: /^Simulating at (\d{2})\/(\d{2})/i,
    phase: 'simulation',
    getProgress: () => 50,
    getMessage: (m) => `Simulating: ${m[1] ?? ''}/${m[2] ?? ''}`,
  },

  // Post-processing
  {
    pattern: /^Writing tabular output/i,
    phase: 'postprocess',
    getProgress: () => 92,
  },
  {
    pattern: /^Computing Life Cycle Costs/i,
    phase: 'postprocess',
    getProgress: () => 94,
  },
  {
    pattern: /^Writing final SQL/i,
    phase: 'postprocess',
    getProgress: () => 96,
  },
  {
    pattern: /^Closing output/i,
    phase: 'postprocess',
    getProgress: () => 98,
  },

  // Completion
  {
    pattern: /^EnergyPlus Completed Successfully/i,
    phase: 'complete',
    getProgress: () => 100,
  },
  {
    pattern: /^EnergyPlus Warmup Error/i,
    phase: 'error',
    getProgress: () => -1,
  },
  {
    pattern: /^EnergyPlus Terminated/i,
    phase: 'error',
    getProgress: () => -1,
  },
];

/**
 * Patterns for console line severity
 */
const SEVERITY_PATTERNS = [
  { pattern: /^\*\*\s*Fatal\s*\*\*/i, type: 'fatal' as const },
  { pattern: /^\*\*\s*Severe\s*\*\*/i, type: 'severe' as const },
  { pattern: /^\*\*\s*Warning\s*\*\*/i, type: 'warning' as const },
  { pattern: /Successfully/i, type: 'success' as const },
];

/**
 * Progress tracker class
 */
export class ProgressTracker {
  private startTime: number;
  private history: ProgressUpdate[] = [];
  private currentPhase: SimulationPhase = 'initializing';
  private currentProgress: number = 0;

  constructor() {
    this.startTime = Date.now();
  }

  /**
   * Reset the tracker
   */
  reset(): void {
    this.startTime = Date.now();
    this.history = [];
    this.currentPhase = 'initializing';
    this.currentProgress = 0;
  }

  /**
   * Parse a line of console output and update progress
   */
  parseLine(line: string): ProgressUpdate | null {
    const trimmedLine = line.trim();
    if (!trimmedLine) return null;

    for (const pattern of PROGRESS_PATTERNS) {
      const match = trimmedLine.match(pattern.pattern);
      if (match) {
        const progress = pattern.getProgress ? pattern.getProgress(match) : this.currentProgress;
        const message = pattern.getMessage ? pattern.getMessage(match) : trimmedLine;

        const update: ProgressUpdate = {
          phase: pattern.phase,
          progress,
          message,
          timestamp: Date.now(),
        };

        // Only add to history if progress actually changed
        if (progress !== this.currentProgress || pattern.phase !== this.currentPhase) {
          this.currentPhase = pattern.phase;
          this.currentProgress = progress;
          this.history.push(update);
        }

        return update;
      }
    }

    return null;
  }

  /**
   * Get console line with severity type
   */
  getConsoleLine(line: string): ConsoleLine {
    let type: ConsoleLine['type'] = 'info';

    for (const { pattern, type: severityType } of SEVERITY_PATTERNS) {
      if (pattern.test(line)) {
        type = severityType;
        break;
      }
    }

    return {
      text: line,
      timestamp: Date.now(),
      type,
    };
  }

  /**
   * Get current progress
   */
  getCurrentProgress(): ProgressUpdate {
    return {
      phase: this.currentPhase,
      progress: this.currentProgress,
      message: this.history[this.history.length - 1]?.message || 'Starting...',
      timestamp: Date.now(),
    };
  }

  /**
   * Get time estimation
   */
  getTimeEstimate(): TimeEstimate {
    const elapsed = Date.now() - this.startTime;

    // Need at least 2 data points and 5% progress to estimate
    if (this.history.length < 2 || this.currentProgress < 5) {
      return {
        elapsed,
        remaining: null,
        total: null,
      };
    }

    // Use recent history for better accuracy
    const recentHistory = this.history.slice(-10);
    if (recentHistory.length < 2) {
      return { elapsed, remaining: null, total: null };
    }

    const firstPoint = recentHistory[0];
    const lastPoint = recentHistory[recentHistory.length - 1];

    if (!firstPoint || !lastPoint) {
      return { elapsed, remaining: null, total: null };
    }

    const timeSpan = lastPoint.timestamp - firstPoint.timestamp;
    const progressSpan = lastPoint.progress - firstPoint.progress;

    if (timeSpan <= 0 || progressSpan <= 0) {
      return { elapsed, remaining: null, total: null };
    }

    // Calculate rate: ms per percent
    const rate = timeSpan / progressSpan;
    const remainingProgress = 100 - this.currentProgress;
    const remaining = remainingProgress * rate;
    const total = elapsed + remaining;

    return {
      elapsed,
      remaining,
      total,
    };
  }

  /**
   * Check if simulation is complete
   */
  isComplete(): boolean {
    return this.currentPhase === 'complete';
  }

  /**
   * Check if simulation has error
   */
  hasError(): boolean {
    return this.currentPhase === 'error';
  }

  /**
   * Get progress history
   */
  getHistory(): ProgressUpdate[] {
    return [...this.history];
  }
}

/**
 * Format time duration for display
 */
export function formatDuration(ms: number): string {
  if (ms < 0) return '--:--';

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  const remainingSeconds = seconds % 60;
  const remainingMinutes = minutes % 60;

  if (hours > 0) {
    return `${String(hours)}:${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  return `${String(remainingMinutes)}:${remainingSeconds.toString().padStart(2, '0')}`;
}

/**
 * Console output buffer with line limit
 */
export class ConsoleBuffer {
  private lines: ConsoleLine[] = [];
  private maxLines: number;

  constructor(maxLines: number = 10000) {
    this.maxLines = maxLines;
  }

  /**
   * Add a line to the buffer
   */
  addLine(line: ConsoleLine): void {
    this.lines.push(line);

    // Trim if over limit
    if (this.lines.length > this.maxLines) {
      this.lines = this.lines.slice(-this.maxLines);
    }
  }

  /**
   * Add text (split into lines)
   */
  addText(text: string, tracker: ProgressTracker): void {
    const textLines = text.split('\n');
    for (const line of textLines) {
      if (line.trim()) {
        this.addLine(tracker.getConsoleLine(line));
      }
    }
  }

  /**
   * Get all lines
   */
  getLines(): ConsoleLine[] {
    return [...this.lines];
  }

  /**
   * Get lines by type
   */
  getLinesByType(type: ConsoleLine['type']): ConsoleLine[] {
    return this.lines.filter((l) => l.type === type);
  }

  /**
   * Get warning and error lines
   */
  getProblems(): ConsoleLine[] {
    return this.lines.filter(
      (l) => l.type === 'warning' || l.type === 'severe' || l.type === 'fatal'
    );
  }

  /**
   * Clear the buffer
   */
  clear(): void {
    this.lines = [];
  }

  /**
   * Get line count
   */
  get length(): number {
    return this.lines.length;
  }

  /**
   * Get as string for copying
   */
  toString(): string {
    return this.lines.map((l) => l.text).join('\n');
  }
}
